/**
 * ============================================================================
 * KbTextBubbleMenu — Bubble menu de formatação inline (estilo GitBook).
 * ----------------------------------------------------------------------------
 * Aparece somente quando há texto selecionado no editor e a seleção NÃO
 * está dentro de nós customizados (callout/steps/table/card/embed/toggle),
 * onde o `KbContextBubble` assume o controle.
 *
/**
 * CHANGELOG:
 *   RELEASE: 2026-07-09 v1.1.2 - updateDelay: 0 no BubbleMenu para eliminar
 *            o debounce de 250ms padrão que fazia a barra "sumir" quando
 *            a seleção mudava rápido (o segundo BubbleMenu do link disputa
 *            reposicionamento). Bubble agora aparece imediatamente.

 *   RELEASE: 2026-07-09 v1.1.1 - Coexistência com KbLinkHoverCard: bubble
 *            continua visível quando a seleção cobre um link, mas oculta
 *            o botão "Link" (evita ação duplicada). Hover-card assume a
 *            edição do link e fica ancorado embaixo (bottom placement).
 *   RELEASE: 2026-07-09 v1.1.0 - Botão de link agora abre o KbLinkEditorDialog
 *            (padrão GitBook) em vez de window.prompt.
 *   RELEASE: 2026-07-09 v1.0.2 - Substitui Radix DropdownMenu (portal) por
 *            menu inline ancorado no próprio container do bubble.
 *   RELEASE: 2026-07-09 v1.0.1 - Preserva seleção do Tiptap (preventDefault
 *            no mousedown) e mantém bubble visível com openRef.
 *   RELEASE: 2026-07-09 v1.0.0 - Versão inicial: substitui a antiga toolbar
 *            fixa do editor por uma barra flutuante contextual.
 * ============================================================================
 */

import { BubbleMenu } from "@tiptap/react/menus";
import type { Editor } from "@tiptap/react";
import {
  Bold, Italic, Underline as UnderlineIcon, Strikethrough, Code,
  Link as LinkIcon, Highlighter, RemoveFormatting, ChevronDown, Type,
  Heading1, Heading2, Heading3,
} from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { openLinkEditor } from "@/lib/kb/pageLinkPicker";

interface Props { editor: Editor | null }

// Botão compacto do bubble (reaproveita o mesmo visual do KbContextBubble)
function Btn({
  onClick, active, title, children,
}: {
  onClick: () => void;
  active?: boolean;
  title: string;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      title={title}
      // Evita perder a seleção do ProseMirror ao clicar no bubble
      onMouseDown={(e) => e.preventDefault()}
      onClick={onClick}
      className={`inline-flex items-center justify-center h-8 w-8 rounded text-xs transition-colors ${
        active
          ? "bg-accent text-accent-foreground"
          : "hover:bg-accent/60 text-foreground"
      }`}
    >
      {children}
    </button>
  );
}

/**
 * Bubble menu de formatação de texto. Só é exibido para seleções não
 * vazias e fora de nós customizados que possuem seu próprio bubble.
 */
export function KbTextBubbleMenu({ editor }: Props) {
  if (!editor) return null;

  return (
    <BubbleMenu
      editor={editor}
      pluginKey="kb-text-bubble"
      shouldShow={({ editor, state }) => {
        const { selection } = state;
        if (selection.empty) return false;
        // Se algum nó customizado estiver ativo, deixa o KbContextBubble atuar.
        const customNodes = [
          "callout", "steps", "stepItem", "table", "tableRow", "tableCell",
          "tableHeader", "cardGrid", "card", "embed", "toggle", "image",
          "codeBlock", "tabs", "tabPanel",
        ];
        if (customNodes.some((n) => editor.isActive(n))) return false;
        return true;
      }}
      updateDelay={0}
      options={{
        placement: "top",
        offset: 8,
      }}

    >

      <div
        data-kb-editor-ui="true"
        className="flex items-center gap-0.5 rounded-md border bg-popover text-popover-foreground shadow-md px-1 py-1"
      >
        {/* Dropdown de estilo de bloco (Parágrafo / H1-H3) — inline, sem portal */}
        <HeadingInlineMenu editor={editor} />

        <div className="w-px h-6 bg-border mx-1" />

        <Btn
          title="Negrito (Ctrl+B)"
          active={editor.isActive("bold")}
          onClick={() => editor.chain().focus().toggleBold().run()}
        >
          <Bold className="h-4 w-4" />
        </Btn>
        <Btn
          title="Itálico (Ctrl+I)"
          active={editor.isActive("italic")}
          onClick={() => editor.chain().focus().toggleItalic().run()}
        >
          <Italic className="h-4 w-4" />
        </Btn>
        <Btn
          title="Sublinhado"
          active={editor.isActive("underline")}
          onClick={() => editor.chain().focus().toggleUnderline().run()}
        >
          <UnderlineIcon className="h-4 w-4" />
        </Btn>
        <Btn
          title="Riscado"
          active={editor.isActive("strike")}
          onClick={() => editor.chain().focus().toggleStrike().run()}
        >
          <Strikethrough className="h-4 w-4" />
        </Btn>
        <Btn
          title="Código inline"
          active={editor.isActive("code")}
          onClick={() => editor.chain().focus().toggleCode().run()}
        >
          <Code className="h-4 w-4" />
        </Btn>
        <Btn
          title="Marca-texto"
          active={editor.isActive("highlight")}
          onClick={() => editor.chain().focus().toggleHighlight().run()}
        >
          <Highlighter className="h-4 w-4" />
        </Btn>

        {/* Botão de link só aparece quando a seleção NÃO está sobre um link.
            Caso contrário, o KbLinkHoverCard assume a edição (evita duplicidade). */}
        {!(editor.isActive("link") || editor.isActive("kbPageLink")) && (
          <>
            <div className="w-px h-6 bg-border mx-1" />

            <Btn
              title="Link"
              onClick={() => {
                // Abre o editor de link (padrão GitBook) em modo "novo".
                const { from, to } = editor.state.selection;
                const selectedText = editor.state.doc.textBetween(from, to, " ");
                openLinkEditor({ kind: "none", text: selectedText }, (result) => {
                  const chain = editor.chain().focus();
                  if (result.kind === "remove") return;
                  if (result.kind === "external") {
                    chain
                      .extendMarkRange("link")
                      .setLink({
                        href: result.href,
                        target: "_blank",
                        rel: "noopener noreferrer",
                      } as any)
                      .run();
                    return;
                  }
                  // result.kind === "page" → substitui o texto selecionado pelo
                  // rótulo do link e aplica a mark kbPageLink em cima.
                  chain
                    .insertContent({
                      type: "text",
                      text: result.title,
                      marks: [{
                        type: "kbPageLink",
                        attrs: {
                          pageId: result.pageId,
                          spaceId: result.spaceId,
                          anchor: result.anchor ?? null,
                          snapshotTitle: result.title,
                        },
                      }],
                    })
                    .run();
                });
              }}
            >
              <LinkIcon className="h-4 w-4" />
            </Btn>
          </>
        )}

        <Btn
          title="Limpar formatação"
          onClick={() => editor.chain().focus().unsetAllMarks().run()}
        >
          <RemoveFormatting className="h-4 w-4" />
        </Btn>
      </div>
    </BubbleMenu>
  );
}

// ---------------------------------------------------------------------------
// Menu inline de estilo de bloco (Parágrafo / Título 1-3)
// Sem portal — ancorado no próprio container do bubble via CSS absolute.
// ---------------------------------------------------------------------------
function HeadingInlineMenu({ editor }: { editor: Editor }) {
  const [open, setOpen] = useState(false);
  const wrapRef = useRef<HTMLDivElement>(null);

  // Rótulo do estilo ativo
  let label = "Parágrafo";
  let Icon: React.ComponentType<{ className?: string }> = Type;
  if (editor.isActive("heading", { level: 1 })) { label = "Título 1"; Icon = Heading1; }
  else if (editor.isActive("heading", { level: 2 })) { label = "Título 2"; Icon = Heading2; }
  else if (editor.isActive("heading", { level: 3 })) { label = "Título 3"; Icon = Heading3; }

  // Fechar ao clicar fora ou pressionar Escape
  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => {
      if (!wrapRef.current) return;
      if (!wrapRef.current.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => { if (e.key === "Escape") setOpen(false); };
    document.addEventListener("mousedown", onDown);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDown);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  const apply = (fn: () => void) => {
    fn();
    setOpen(false);
  };

  // Item do menu (mesmo visual do DropdownMenuItem do shadcn)
  const Item = ({
    onSelect, children,
  }: { onSelect: () => void; children: React.ReactNode }) => (
    <button
      type="button"
      onMouseDown={(e) => e.preventDefault()}
      onClick={onSelect}
      className="w-full flex items-center px-2 py-1.5 text-sm rounded-sm hover:bg-accent hover:text-accent-foreground text-left"
    >
      {children}
    </button>
  );

  return (
    <div ref={wrapRef} className="relative">
      <button
        type="button"
        onMouseDown={(e) => e.preventDefault()}
        onClick={() => setOpen((v) => !v)}
        className="inline-flex items-center gap-1 h-8 px-2 rounded text-xs font-medium hover:bg-accent/60"
        title="Estilo de bloco"
      >
        <Icon className="h-3.5 w-3.5" />
        <span>{label}</span>
        <ChevronDown className="h-3 w-3" />
      </button>

      {open && (
        <div
          data-kb-editor-ui="true"
          className="absolute top-full left-0 mt-1 z-[70] w-40 rounded-md border bg-popover text-popover-foreground shadow-md p-1"
        >
          <Item onSelect={() => apply(() => editor.chain().focus().setParagraph().run())}>
            <Type className="h-4 w-4 mr-2" /> Parágrafo
          </Item>
          <Item onSelect={() => apply(() => editor.chain().focus().toggleHeading({ level: 1 }).run())}>
            <Heading1 className="h-4 w-4 mr-2" /> Título 1
          </Item>
          <Item onSelect={() => apply(() => editor.chain().focus().toggleHeading({ level: 2 }).run())}>
            <Heading2 className="h-4 w-4 mr-2" /> Título 2
          </Item>
          <Item onSelect={() => apply(() => editor.chain().focus().toggleHeading({ level: 3 }).run())}>
            <Heading3 className="h-4 w-4 mr-2" /> Título 3
          </Item>
        </div>
      )}
    </div>
  );
}
